import { StrictMode, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import './styles.css';
import { SessionProvider, useSession } from './lib/session';
import { Loading, ToastProvider } from './components/ui';
import { Layout } from './components/Layout';
import { ForgotPassword, Login, MfaVerify, ResetPassword, Signup } from './pages/Auth';
import { LegalAccept, LegalPage } from './pages/Legal';
import { Inbox } from './pages/Inbox';
import { InvoiceDetail } from './pages/InvoiceDetail';
import { Advisor, Dashboard } from './pages/Dashboard';
import { VendorDetail, Vendors } from './pages/Vendors';
import { Accounting, Audit, Bank, DeliveryNotes, EInvoice, Messages, Payments } from './pages/Operations';
import { Settings } from './pages/Settings';
import { PublicApproval, PublicStatus } from './pages/Public';

/** En el entorno de prueba, estas pantallas no existen: se vuelve a la bandeja. */
function SinPrueba({ children }: { children: ReactNode }) {
  const s = useSession();
  return s.prueba ? <Navigate to="/" replace /> : <>{children}</>;
}

function Protected({ children }: { children: ReactNode }) {
  const s = useSession();
  const loc = useLocation();
  if (s.loading) return <Loading />;
  if (!s.me) return <Navigate to="/entrar" state={{ from: loc.pathname }} replace />;
  if (!s.me.mfaVerified) return <Navigate to="/verificar" replace />;
  // Si el rol exige segundo factor y aún no está activado, solo se puede entrar en Ajustes para activarlo
  if (s.me.mfaEnrollmentRequired && loc.pathname !== '/ajustes') return <Navigate to="/ajustes?seguridad=1" replace />;
  // Textos legales actualizados: el administrador debe aceptarlos antes de seguir
  if (!s.me.mfaEnrollmentRequired && s.me.legal?.mustAccept) return <Navigate to="/aceptar-condiciones" replace />;
  return <>{children}</>;
}

function App() {
  return (
    <Routes>
      <Route path="/entrar" element={<Login />} />
      <Route path="/alta" element={<Signup />} />
      <Route path="/verificar" element={<MfaVerify />} />
      <Route path="/recuperar" element={<ForgotPassword />} />
      <Route path="/recuperar/:token" element={<ResetPassword />} />
      <Route path="/legal/:doc" element={<LegalPage />} />
      <Route path="/aceptar-condiciones" element={<LegalAccept />} />
      <Route path="/aprobar/:token" element={<PublicApproval />} />
      <Route path="/estado/:token" element={<PublicStatus />} />
      <Route element={<Protected><Layout /></Protected>}>
        <Route index element={<Inbox />} />
        <Route path="aprobaciones" element={<Inbox mine />} />
        <Route path="facturas/:id" element={<InvoiceDetail />} />
        <Route path="panel" element={<Dashboard />} />
        <Route path="asesoria" element={<Advisor />} />
        <Route path="proveedores" element={<Vendors />} />
        <Route path="proveedores/:id" element={<VendorDetail />} />
        <Route path="contabilidad" element={<SinPrueba><Accounting /></SinPrueba>} />
        <Route path="factura-electronica" element={<SinPrueba><EInvoice /></SinPrueba>} />
        <Route path="pagos" element={<SinPrueba><Payments /></SinPrueba>} />
        <Route path="banco" element={<SinPrueba><Bank /></SinPrueba>} />
        <Route path="albaranes" element={<SinPrueba><DeliveryNotes /></SinPrueba>} />
        <Route path="mensajes" element={<SinPrueba><Messages /></SinPrueba>} />
        <Route path="auditoria" element={<SinPrueba><Audit /></SinPrueba>} />
        <Route path="ajustes" element={<Settings />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <ToastProvider>
        <SessionProvider>
          <App />
        </SessionProvider>
      </ToastProvider>
    </BrowserRouter>
  </StrictMode>,
);
